import React, { useMemo, useState } from "react";

const slotColors = {
  available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lunchBooked: "bg-amber-50 text-amber-700 border-amber-200",
  dinnerBooked: "bg-sky-50 text-sky-700 border-sky-200",
  fullDayBooked: "bg-rose-50 text-rose-700 border-rose-200",
  blocked: "bg-slate-100 text-slate-600 border-slate-300",
  inactive: "bg-slate-50 text-slate-400 border-slate-100",
};

const slotStatusLabels = {
  available: "Available",
  lunchBooked: "Lunch Booked",
  dinnerBooked: "Dinner Booked",
  fullDayBooked: "Full Day Booked",
  blocked: "Blocked",
};

const formatKey = (date) => date.toISOString().split("T")[0];
const addDays = (date, count) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + count);
  return copy;
};

const monthLabel = new Intl.DateTimeFormat("en-IN", { month: "long", year: "numeric" });
const dayLabel = new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const getStatus = (slot = {}) => {
  // Owner dashboard already stores the exact status (available/lunchBooked/dinnerBooked/
  // fullDayBooked/blocked) alongside the lunch/dinner booleans — prefer it so "Blocked"
  // dates don't get shown as indistinguishable from "Full Day Booked" ones.
  if (slot.status && slotColors[slot.status]) {
    return slot.status;
  }

  if (slot.lunch && slot.dinner) {
    return "fullDayBooked";
  }

  if (slot.lunch && !slot.dinner) {
    return "lunchBooked";
  }

  if (!slot.lunch && slot.dinner) {
    return "dinnerBooked";
  }

  return "available";
};

const buildCalendarDays = (monthDate, bookedSlots) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const startDate = addDays(firstDay, -startOffset);

  return Array.from({ length: 42 }, (_, index) => {
    const date = addDays(startDate, index);
    const key = formatKey(date);
    return {
      date,
      key,
      currentMonth: date.getMonth() === month,
      slot: bookedSlots[key] || { lunch: false, dinner: false },
    };
  });
};

const getNextDateFor = (bookedSlots, predicate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let step = 0; step < 120; step += 1) {
    const candidate = addDays(today, step);
    const slot = bookedSlots[formatKey(candidate)] || { lunch: false, dinner: false };
    if (predicate(slot)) {
      return dayLabel.format(candidate);
    }
  }

  return "Contact us for upcoming dates";
};

const legend = [
  ["available", "Available"],
  ["lunchBooked", "Lunch Booked"],
  ["dinnerBooked", "Dinner Booked"],
  ["fullDayBooked", "Full Day Booked"],
  ["blocked", "Blocked"],
];

export default function BanquetAvailabilityCalendar({ bookedSlots = {}, onSelectSlot = () => {}, selectedSlot }) {
  const [activeMonth, setActiveMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const days = useMemo(() => buildCalendarDays(activeMonth, bookedSlots), [activeMonth, bookedSlots]);
  const summary = useMemo(
    () => ({
      lunch: getNextDateFor(bookedSlots, (slot) => !slot.lunch),
      dinner: getNextDateFor(bookedSlots, (slot) => !slot.dinner),
      both: getNextDateFor(bookedSlots, (slot) => !slot.lunch && !slot.dinner),
    }),
    [bookedSlots]
  );

  return (
    <section className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-3 shadow-sm sm:rounded-[32px] sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="break-words text-xs font-semibold uppercase tracking-[0.14em] text-emerald-600 sm:tracking-[0.24em]">Availability Calendar</p>
          <h2 className="mt-2 break-words text-xl font-semibold text-slate-900 sm:text-2xl">Lunch and dinner shown separately</h2>
        </div>
        <div className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 sm:w-auto">
          <button
            type="button"
            onClick={() => setActiveMonth(new Date(activeMonth.getFullYear(), activeMonth.getMonth() - 1, 1))}
            className="min-h-[44px] rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 sm:px-4"
          >
            Prev
          </button>
          <div className="truncate rounded-full bg-slate-100 px-3 py-2 text-center text-sm font-semibold text-slate-700 sm:px-4">
            {monthLabel.format(activeMonth)}
          </div>
          <button
            type="button"
            onClick={() => setActiveMonth(new Date(activeMonth.getFullYear(), activeMonth.getMonth() + 1, 1))}
            className="min-h-[44px] rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 sm:px-4"
          >
            Next
          </button>
        </div>
      </div>

      <div className="mt-6 grid min-w-0 gap-3 sm:grid-cols-3">
        <div className="min-w-0 rounded-3xl bg-emerald-50 p-4">
          <p className="break-words text-xs font-semibold uppercase tracking-[0.12em] text-emerald-700 sm:tracking-[0.22em]">Next Lunch</p>
          <p className="mt-2 break-words text-sm font-semibold text-slate-900">{summary.lunch}</p>
        </div>
        <div className="min-w-0 rounded-3xl bg-sky-50 p-4">
          <p className="break-words text-xs font-semibold uppercase tracking-[0.12em] text-sky-700 sm:tracking-[0.22em]">Next Dinner</p>
          <p className="mt-2 break-words text-sm font-semibold text-slate-900">{summary.dinner}</p>
        </div>
        <div className="min-w-0 rounded-3xl bg-amber-50 p-4">
          <p className="break-words text-xs font-semibold uppercase tracking-[0.12em] text-amber-700 sm:tracking-[0.22em]">Next Fully Free</p>
          <p className="mt-2 break-words text-sm font-semibold text-slate-900">{summary.both}</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {legend.map(([key, label]) => (
          <span key={key} className={`min-h-[36px] rounded-full border px-3 py-2 text-xs font-semibold ${slotColors[key]}`}>
            {label}
          </span>
        ))}
      </div>

      <div className="mt-5 grid min-w-0 grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-normal text-slate-400 sm:gap-2 sm:text-xs">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
          <div key={day} className="min-w-0 truncate">{day}</div>
        ))}
      </div>

      <div className="mt-3 grid min-w-0 grid-cols-7 gap-1 sm:gap-2">
        {days.map((day) => {
          const status = day.currentMonth ? getStatus(day.slot) : "inactive";
          const isSelected = selectedSlot?.date === day.key;
          return (
            <button
              key={day.key}
              type="button"
              disabled={!day.currentMonth}
              onClick={() => onSelectSlot(day.key, day.slot)}
              className={`min-h-[46px] min-w-0 rounded-xl border p-1 text-left transition sm:min-h-[92px] sm:rounded-3xl sm:p-2 ${
                slotColors[status]
              } ${isSelected ? "ring-2 ring-emerald-500" : ""}`}
            >
              <div className="text-xs font-semibold sm:text-sm">{day.date.getDate()}</div>
              {day.currentMonth && (
                <div className="mt-1 hidden text-[11px] font-medium leading-4 sm:block">{slotStatusLabels[status] || slotStatusLabels.available}</div>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-6 min-w-0 rounded-3xl bg-slate-50 p-4">
        <p className="text-sm font-semibold text-slate-900">Selected preference</p>
        <p className="mt-2 break-words text-sm text-slate-600">
          {selectedSlot
            ? `${dayLabel.format(new Date(selectedSlot.date))} - ${selectedSlot.slotLabel}`
            : "Tap any date to keep the inquiry ready with date and slot context."}
        </p>
      </div>
    </section>
  );
}
